import { FC, useCallback, useMemo, useState, type JSX } from 'react';
import { useDispatch } from 'react-redux';

import * as jobContentActions from '@payaca/store/jobContent/jobContentActions';
import { JobContent, JobLineItemBase } from '@payaca/types/jobContentTypes';

import { Button, Modal } from '@payaca/ambrosia';
import ItemGroupHeader from '../itemGroupHeader/ItemGroupHeader';
import JobLineItemsControl from '../jobLineItemsControl/JobLineItemsControl';

import {
  getJobContent as getJobContentFromState,
  getJobLineItemGroup,
} from '@/utils/stateAccessors';

import { useSelector } from '@/api/state';
import './JobLineItemGroupControl.css';

type Props = {
  canDeleteItemGroup: boolean;
  jobIsInvoice: boolean;
  jobLineItemGroupId: number;
  jobContentId: number;
  handleOpenCreateJobLineItemModal: (
    jobLineItemId: number,
    initialItemData?: Partial<JobLineItemBase>
  ) => void;
  handleCreateJobLineItemAndOpenCreateJobLineItemModal: (
    lineItemId: number | null,
    initialItemData?: Partial<JobLineItemBase>
  ) => void;
  handleOpenAdvancedSearchLineItemModal: (searchTerm?: string) => void;
};

const JobLineItemGroupControl: FC<Props> = ({
  canDeleteItemGroup,
  jobLineItemGroupId,
  jobIsInvoice,
  jobContentId,
  handleOpenCreateJobLineItemModal,
  handleCreateJobLineItemAndOpenCreateJobLineItemModal,
  handleOpenAdvancedSearchLineItemModal,
}: Props): JSX.Element | null => {
  const dispatch = useDispatch();
  const [isDeleting, setIsDeleting] = useState(false);
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);

  const jobLineItemGroup = useSelector((state) =>
    getJobLineItemGroup(state, jobLineItemGroupId)
  );

  const jobContent: JobContent | undefined = useSelector((state) =>
    getJobContentFromState(state, jobContentId)
  );

  const jobContentFetchSucceededAt = useSelector(
    (state) =>
      jobLineItemGroup &&
      state.jobContent?.jobContents &&
      state.jobContent.jobContents[jobLineItemGroup.jobContentId]
        ?.fetchSucceededAt
  );

  const jobLineItemGroupFetchSucceededAt = useSelector(
    (state) =>
      state.jobContent?.jobLineItemGroups &&
      state.jobContent.jobLineItemGroups[jobLineItemGroupId]?.fetchSucceededAt
  );

  const isDeletingJobLineItemGroup = useSelector((state) => {
    return state.jobContent.isDeletingJobLineItemGroup;
  });

  const lastExternalUpdateTimestamp = useMemo(() => {
    const groupFetchTime = jobLineItemGroupFetchSucceededAt
      ? new Date(jobLineItemGroupFetchSucceededAt).getTime()
      : 0;
    const contentFetchTime = jobContentFetchSucceededAt
      ? new Date(jobContentFetchSucceededAt).getTime()
      : 0;
    if (groupFetchTime === 0 && contentFetchTime === 0) return undefined;
    return new Date(Math.max(groupFetchTime, contentFetchTime));
  }, [jobLineItemGroupFetchSucceededAt, jobContentFetchSucceededAt]);

  const getJobContent = useCallback(() => {
    dispatch(jobContentActions.requestGetJobContent(jobContentId));
    setIsDeleting(false);
  }, [dispatch, jobContentId]);

  const lineItemGroupHasAnyItems = useMemo(() => {
    if (!jobLineItemGroup?.jobLineItemIds) return false;
    return jobLineItemGroup.jobLineItemIds.length > 0;
  }, [jobLineItemGroup]);

  const deleteJobLineItemGroup = useCallback(() => {
    setIsDeleting(true);

    dispatch(
      jobContentActions.requestDeleteJobLineItemGroup(
        jobLineItemGroupId,
        getJobContent
      )
    );
  }, [dispatch, jobLineItemGroupId, getJobContent]);

  const handleDeleteJobLineItemGroup = useCallback(() => {
    if (lineItemGroupHasAnyItems) {
      setShowConfirmDeleteModal(true);
    } else {
      deleteJobLineItemGroup();
    }
  }, [lineItemGroupHasAnyItems, deleteJobLineItemGroup]);

  const updateJobLineItemGroup = useCallback(
    (description: string) => {
      dispatch(
        jobContentActions.requestUpdateJobLineItemGroup(
          {
            description: description,
            jobLineItemGroupId: jobLineItemGroupId,
          },
          () =>
            dispatch(
              jobContentActions.requestGetJobLineItemGroupsForJobContent(
                jobContentId
              )
            )
        )
      );
    },
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'jobContentId'
    [dispatch, jobLineItemGroupId]
  );

  return (
    <div className="job-line-item-group-control">
      {jobLineItemGroup && (
        <>
          <ItemGroupHeader
            canDeleteItemGroup={canDeleteItemGroup}
            isDeleting={isDeleting && isDeletingJobLineItemGroup}
            itemGroup={jobLineItemGroup}
            onDeleteItemGroup={handleDeleteJobLineItemGroup}
            onUpdateItemGroup={(value: { [key: string]: any }) =>
              updateJobLineItemGroup(value.description)
            }
            itemGroupExternalUpdateTimestamp={lastExternalUpdateTimestamp}
          />
          <JobLineItemsControl
            jobLineItemGroupId={jobLineItemGroupId}
            jobIsInvoice={jobIsInvoice}
            handleOpenCreateJobLineItemModal={handleOpenCreateJobLineItemModal}
            handleOpenAdvancedSearchLineItemModal={
              handleOpenAdvancedSearchLineItemModal
            }
            handleCreateJobLineItemAndOpenCreateJobLineItemModal={
              handleCreateJobLineItemAndOpenCreateJobLineItemModal
            }
            disabledItemIds={jobContent?.lineItemIds || []}
          />
        </>
      )}
      <Modal
        isOpen={showConfirmDeleteModal}
        title="Confirm group delete"
        onClose={() => setShowConfirmDeleteModal(false)}
        size="sm"
      >
        <Modal.Body>
          <p>
            Are you sure you want to delete the group? This group contains one
            or more items which will also be deleted.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Modal.Footer.Actions>
            <Button
              variant="outline"
              onClick={() => setShowConfirmDeleteModal(false)}
            >
              Don&apos;t delete group
            </Button>
            <Button
              onClick={() => {
                setShowConfirmDeleteModal(false);
                deleteJobLineItemGroup();
              }}
            >
              Yes, delete the group
            </Button>
          </Modal.Footer.Actions>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
export default JobLineItemGroupControl;
